import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import { parseCsv, toCsv, downloadCsv } from "../../../utils/csv";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface DiagnosisRow {
  id: number;
  code?: string;
  description: string;
  master_id?: string;
  category?: string;
  standard_code?: string;
  is_active?: boolean;
}

interface FormState {
  code: string;
  description: string;
  master_id: string;
  category: string;
  standard_code: string;
}

export default function Diagnosis() {
  const { showToast } = useToast();
  const [rows, setRows] = useState<DiagnosisRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const importRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<FormState>({
    code: "",
    description: "",
    master_id: "",
    category: "",
    standard_code: ""
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/api/admin/diagnosis") as DiagnosisRow[];
      const list = Array.isArray(data) ? data : [];
      setRows(list);
    } catch (e) {
      showToast({ message: "Failed to load diagnosis list", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = () => {
    if (!rows.length) {
      showToast({ message: "No data to export.", type: "warning" });
      return;
    }
    const headers = ["code", "description", "category", "standard_code", "master_id", "is_active"];
    const data = rows.map((r) => ({
      code: r.code || "",
      description: r.description,
      category: r.category || "",
      standard_code: r.standard_code || "",
      master_id: r.master_id || "",
      is_active: r.is_active ?? true,
    }));
    downloadCsv(toCsv(headers, data), "diagnosis.csv");
    showToast({ message: "Export downloaded.", type: "info" });
  };

  const handleImportFile = async (file: File) => {
    const text = await file.text();
    const csvRows = parseCsv(text);
    if (csvRows.length < 2) return showToast({ message: "CSV is empty", type: "error" });
    const headers = csvRows[0].map((h) => h.trim().toLowerCase());
    const dataRows = csvRows.slice(1);

    showToast({ message: "Importing CSV data...", type: "info" });
    let errorCount = 0;

    for (const row of dataRows) {
      const get = (key: string) => {
        const idx = headers.indexOf(key);
        return idx >= 0 ? (row[idx] ?? "").trim() : "";
      };
      const description = get("description") || get("name");
      if (!description) continue;
      const payload = {
        code: get("code") || undefined,
        description,
        category: get("category") || undefined,
      };

      try {
        const existing = rows.find((r) => r.description === description || (payload.code && r.code === payload.code));
        if (existing) {
          await apiPut(`/api/admin/diagnosis/${existing.id}`, payload);
        } else {
          await apiPost("/api/admin/diagnosis", payload);
        }
      } catch (e) {
        errorCount++;
        console.error("Row import failed", row, e);
      }
    }
    
    if (errorCount > 0) {
      showToast({ message: `Import finished with ${errorCount} errors.`, type: "warning" });
    } else {
      showToast({ message: "Import successful.", type: "success" });
    }
    load();
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) return showToast({ message: "Description required", type: "warning" });

    const payload = {
      code: form.code,
      description: form.description,
      master_id: form.master_id || null,
      category: form.category || null,
      standard_code: form.standard_code || null
    };

    try {
      if (editingId) {
        await apiPut(`/api/admin/diagnosis/${editingId}`, payload);
        showToast({ message: "Diagnosis Updated", type: "success" });
      } else {
        await apiPost("/api/admin/diagnosis", payload);
        showToast({ message: "Diagnosis Added", type: "success" });
      }
      setForm({ code: "", description: "", master_id: "", category: "", standard_code: "" });
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (e) {
      showToast({ message: "Operation failed", type: "error" });
    }
  };

  const handleEdit = (row: DiagnosisRow) => {
    setForm({
      code: row.code || "",
      description: row.description,
      master_id: row.master_id || "",
      category: row.category || "",
      standard_code: row.standard_code || "",
    });
    setEditingId(row.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggle = async (row: DiagnosisRow) => {
    try {
      await apiPut(`/api/admin/diagnosis/${row.id}`, {
        is_active: !row.is_active
      });
      load();
    } catch (e) {
      showToast({ message: "Failed to toggle status", type: "error" });
    }
  };

  const remove = async (row: DiagnosisRow) => {
    if (!window.confirm(`Delete "${row.description}"?`)) return;
    try {
      await apiDelete(`/api/admin/diagnosis/${row.id}`);
      showToast({ message: "Diagnosis deleted", type: "success" });
      load();
    } catch (e) {
      showToast({ message: "Failed to delete diagnosis", type: "error" });
    }
  };

  const filtered = rows.filter(r =>
    r.description.toLowerCase().includes(search.toLowerCase()) ||
    (r.code && r.code.toLowerCase().includes(search.toLowerCase())) ||
    (r.master_id && r.master_id.toLowerCase().includes(search.toLowerCase())) ||
    (r.category && r.category.toLowerCase().includes(search.toLowerCase())) ||
    (r.standard_code && r.standard_code.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="master-panel">
      {/* Toolbar */}
      <div className="master-toolbar">
        <input
          type="search"
          placeholder="Search diagnosis..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="master-search"
        />
        <div className="master-toolbar-actions">
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            className="master-file-input"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) handleImportFile(file);
              if (e.target) e.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => importRef.current?.click()}>Import CSV</Button>
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          <Button variant="primary" onClick={() => { setShowForm(!showForm); if (!showForm) { setForm({ code: "", description: "", master_id: "", category: "", standard_code: "" }); setEditingId(null); } }}>
            {showForm ? "Cancel Form" : "+ Add Diagnosis"}
          </Button>
        </div>
      </div>

      {showForm && (
        <div className={`master-form-row ${editingId ? "is-editing" : ""}`}>
          <div className="master-form-grid master-form-grid-diagnosis">
            <input
              placeholder="Code (e.g. K02.1)"
              value={form.code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, code: e.target.value })}
              className="master-input"
            />
            <input
              placeholder="Description (e.g. Dental Caries)"
              value={form.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, description: e.target.value })}
              className="master-input"
            />
            <input
              placeholder="Category"
              value={form.category}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, category: e.target.value })}
              className="master-input"
            />
          </div>
          <div className="master-form-grid master-form-grid-diagnosis-secondary">
            <input
              placeholder="Master ID"
              value={form.master_id}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, master_id: e.target.value })}
              className="master-input"
            />
            <input
              placeholder="Standard Code (e.g. ICD10:K02.1)"
              value={form.standard_code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, standard_code: e.target.value })}
              className="master-input"
            />
            <div className="master-form-actions">
              <Button variant="primary" onClick={handleSubmit}>
                {editingId ? "💾 Update" : "➕ Add"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="master-table-wrap">
        <table className="master-table">
          <thead className="master-thead">
            <tr>
              <th className="master-th">Code</th>
              <th className="master-th">Description</th>
              <th className="master-th">Master ID</th>
              <th className="master-th">Category</th>
              <th className="master-th">Standard Code</th>
              <th className="master-th">Status</th>
              <th className="master-th master-th-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="master-empty">Loading diagnosis records...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="master-empty">No records found.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className={`master-row ${editingId === r.id ? "is-editing" : ""}`}>
                  <td className="master-cell master-accent">{r.code || "—"}</td>
                  <td className="master-cell master-strong">{r.description}</td>
                  <td className="master-cell">
                    <code className="master-code">{r.master_id || "—"}</code>
                  </td>
                  <td className="master-cell">
                    {r.category ? <span className="master-pill">{r.category}</span> : "—"}
                  </td>
                  <td className="master-cell master-muted">{r.standard_code || "—"}</td>
                  <td className="master-cell">
                    <span 
                      className={`master-status ${r.is_active ? "is-active" : "is-inactive"}`}
                      onClick={() => toggle(r)}
                    >
                      {r.is_active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="master-cell master-cell-right">
                    <div className="master-actions">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(r)}>Edit</Button>
                      <Button variant="danger" size="sm" onClick={() => remove(r)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
